// ============================================================================
// DOCUMENT GENERATION HOOK
// AI-powered content generation for Smart Docs
// ============================================================================

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { docsApi } from '@/lib/api/client'
import { useSessionStore } from '@/stores/sessionStore'
import { useAIStore } from '@/stores/aiStore'
import type { JSONContent } from '@tiptap/react'

// ============================================================================
// TYPES
// ============================================================================

export interface GeneratedSection {
  sectionId: string
  heading: string
  content: string
  embeddedEntities: Array<{
    label: string
    classUri: string
    className: string
    domain: 'market' | 'business' | 'solution'
    position: { start: number; end: number }
  }>
}

export interface GenerationResult {
  success: boolean
  sections: GeneratedSection[]
  tiptapContent: JSONContent
  plainText: string
  entityCount: number
  error?: string
}

export interface UseDocumentGenerationOptions {
  onSuccess?: (result: GenerationResult) => void
  onError?: (error: Error) => void
}

// ============================================================================
// HOOK
// ============================================================================

export function useDocumentGeneration(options?: UseDocumentGenerationOptions) {
  const { accountId, projectId } = useSessionStore()
  const apiKey = useAIStore((state) => state.apiKey)
  const [currentSection, setCurrentSection] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async ({
      docId,
      templateId,
      documentTitle,
      sectionIds,
      includeEntities = true,
    }: {
      docId: string
      templateId: string
      documentTitle?: string
      sectionIds?: string[]
      includeEntities?: boolean
    }) => {
      if (!accountId || !projectId) {
        throw new Error('No account or project selected')
      }
      if (!apiKey) {
        throw new Error('API key not configured. Please add your API key in Settings.')
      }
      if (!templateId) {
        throw new Error('Template ID is required for content generation')
      }

      // Track which section is being generated (first one or all)
      setCurrentSection(sectionIds?.[0] || 'all')

      const result = await docsApi.generateContent(accountId, projectId, docId, {
        apiKey,
        templateId,
        documentTitle,
        sectionIds,
        includeEntities,
      })

      setCurrentSection(null)

      if (!result.success) {
        throw new Error(result.error || 'Generation failed')
      }

      return result as GenerationResult
    },
    onSuccess: (result) => {
      options?.onSuccess?.(result)
    },
    onError: (error: Error) => {
      setCurrentSection(null)
      options?.onError?.(error)
    },
  })

  return {
    // Mutation state
    generate: mutation.mutate,
    generateAsync: mutation.mutateAsync,
    isGenerating: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    result: mutation.data,

    // Generation progress
    currentSection,

    // Reset
    reset: mutation.reset,
  }
}

export default useDocumentGeneration
